import React, { createContext, useContext, useState, useEffect } from 'react';
import { storage } from '@/shared/utils/storage';

export interface ThemeClasses {
  // Backgrounds
  bgPrimary: string;
  bgSecondary: string;
  bgTertiary: string;
  
  // Text
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  
  // Borders
  borderPrimary: string;
  borderSecondary: string;
  
  // Accents
  accentBlue: string;
  accentGreen: string;
  accentRed: string;
  accentYellow: string;
  accentPurple: string;

  // Interaction
  surfaceHover: string;
  surfaceSelected: string;
  controlActive: string;
  selectionRing: string;
  primaryAction: string;
  infoState: string;
  
  // Interactive
  hover: string;
  hoverBg: string;
  activeBg: string;
  
  // Components
  card: string;
  cardHover: string;
  button: string;
  buttonHover: string;
  focusRing: string;
}

interface ThemeContextType {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  theme: ThemeClasses;
}

// Unified theme classes that work for both old and new components
const lightTheme: ThemeClasses = {
  // Backgrounds - keep the page light without flattening every surface into gray
  bgPrimary: 'bg-zinc-50',
  bgSecondary: 'bg-white',
  bgTertiary: 'bg-zinc-100',
  
  // Text - darker hierarchy for stronger readability in light mode
  textPrimary: 'text-zinc-950',
  textSecondary: 'text-zinc-800',
  textMuted: 'text-zinc-600',
  
  // Borders - lighter and quieter so chrome does not feel tacky
  borderPrimary: 'border-zinc-200',
  borderSecondary: 'border-zinc-300',
  
  // Accents - slightly denser fills so chips and emphasis blocks read faster
  accentBlue: 'text-blue-800 bg-blue-100',
  accentGreen: 'text-green-800 bg-green-100',
  accentRed: 'text-red-800 bg-red-100',
  accentYellow: 'text-amber-800 bg-amber-100',
  accentPurple: 'text-purple-800 bg-purple-100',

  // Interaction
  surfaceHover: 'hover:bg-zinc-100 hover:text-zinc-950',
  surfaceSelected: '!border-transparent !bg-blue-100 !text-blue-900',
  controlActive: '!border-transparent !bg-blue-100 !text-blue-900',
  selectionRing: '',
  primaryAction: 'bg-blue-600 text-white hover:bg-blue-700',
  infoState: 'text-blue-700',
  
  // Interactive - soft light-mode motion/state changes
  hover: 'hover:bg-zinc-100 hover:text-zinc-950',
  hoverBg: 'bg-zinc-100',
  activeBg: 'bg-blue-100',
  
  // Components - seamless surfaces with depth instead of outlines
  card: 'bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_rgba(15,23,42,0.04)]',
  cardHover: 'hover:shadow-[0_2px_6px_rgba(15,23,42,0.06),0_12px_28px_rgba(15,23,42,0.06)]',
  button: 'bg-blue-600 text-white hover:bg-blue-700',
  buttonHover: 'hover:bg-blue-700',
  focusRing: 'focus:outline-none focus:ring-2 focus:ring-zinc-300/80 focus:ring-offset-2',
};

const darkTheme: ThemeClasses = {
  // Backgrounds - matching both notion-* and gray-* patterns
  bgPrimary: 'bg-gray-900 dark:bg-notion-bg-primary',
  bgSecondary: 'bg-gray-800 dark:bg-notion-bg-secondary',
  bgTertiary: 'bg-gray-700 dark:bg-notion-bg-tertiary',
  
  // Text
  textPrimary: 'text-gray-100 dark:text-notion-text-primary',
  textSecondary: 'text-gray-400 dark:text-notion-text-secondary',
  // Muted stays below secondary (#9b9b9b) but keeps 4.5:1 on every dark surface up to #2a2a2a.
  textMuted: 'text-[#939393]',
  
  // Borders
  borderPrimary: 'border-gray-800 dark:border-notion-border-primary',
  borderSecondary: 'border-gray-700',
  
  // Accents
  accentBlue: 'text-blue-400 bg-blue-500/20 dark:text-notion-accent-blue dark:bg-notion-accent-blue/20',
  accentGreen: 'text-green-400 bg-green-500/20 dark:bg-notion-accent-green/20',
  accentRed: 'text-red-400 bg-red-500/20',
  accentYellow: 'text-yellow-400 bg-yellow-500/20',
  accentPurple: 'text-purple-400 bg-purple-500/20',

  // Interaction
  surfaceHover: 'hover:bg-white/[0.08] hover:text-[#f5f7fb] dark:hover:bg-white/[0.08] dark:hover:text-[#f5f7fb]',
  surfaceSelected: '!border-transparent !bg-blue-500/20 !text-blue-50 dark:!border-transparent dark:!bg-blue-500/20 dark:!text-blue-50',
  controlActive: '!border-transparent !bg-blue-500/20 !text-blue-50 dark:!border-transparent dark:!bg-blue-500/20 dark:!text-blue-50',
  selectionRing: '',
  // White text needs a blue at least as deep as blue-600; the Notion accent blue gave 2.7:1.
  primaryAction: 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700',
  infoState: 'text-blue-400 dark:text-notion-accent-blue',
  
  // Interactive
  hover: 'hover:bg-white/[0.08] hover:text-[#f5f7fb] dark:hover:bg-white/[0.08] dark:hover:text-[#f5f7fb]',
  hoverBg: 'bg-white/[0.08] dark:bg-white/[0.08]',
  activeBg: 'bg-blue-500/20 dark:bg-blue-500/20',
  
  // Components
  card: 'bg-gray-800 dark:bg-notion-bg-secondary shadow-[0_1px_2px_rgba(0,0,0,0.28),0_12px_32px_rgba(0,0,0,0.22)]',
  cardHover: 'hover:shadow-[0_2px_6px_rgba(0,0,0,0.32),0_16px_36px_rgba(0,0,0,0.26)]',
  button: 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700',
  buttonHover: 'hover:bg-blue-700 dark:hover:bg-blue-600',
  focusRing: 'focus:outline-none focus:ring-2 focus:ring-zinc-300/80 dark:focus:ring-zinc-600/70 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [savedDarkMode, setSavedDarkMode] = useState(() => {
    const appState = storage.load();
    return appState?.settings?.darkMode ?? true; // Default to dark mode like Notion
  });
  const [embedAppearance, setEmbedAppearance] = useState<'light' | 'dark' | null>(null);
  const isDarkMode = embedAppearance === null ? savedDarkMode : embedAppearance === 'dark';

  useEffect(() => {
    if (window.parent === window) return;
    const saiOrigin = 'https://sai.dope-ai.kr';
    const onAppearance = (event: MessageEvent<unknown>) => {
      if (event.source !== window.parent || event.origin !== saiOrigin) return;
      if (!event.data || typeof event.data !== 'object') return;
      const message = event.data as Record<string, unknown>;
      if (message.type !== 'sai:appearance' || !['light', 'dark'].includes(message.appearance as string)) return;
      setEmbedAppearance(message.appearance as 'light' | 'dark');
    };
    window.addEventListener('message', onAppearance);
    window.parent.postMessage({ type: 'pm:appearance-ready' }, saiOrigin);
    return () => window.removeEventListener('message', onAppearance);
  }, []);

  useEffect(() => {
    // Update unified storage when dark mode changes
    const appState = storage.load();
    storage.update({
      settings: {
        ...appState?.settings,
        darkMode: savedDarkMode,
      },
    });
  }, [savedDarkMode]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    if (embedAppearance === null) setSavedDarkMode((value) => !value);
  };

  const theme = isDarkMode ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode, theme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Helper function to combine theme classes
// eslint-disable-next-line react-refresh/only-export-components
export const cn = (...classes: (string | undefined | false)[]) => {
  return classes.filter(Boolean).join(' ');
}; 
